/* eslint-disable jsx-a11y/click-events-have-key-events */

import { Fragment, useRef } from "react";
import classes from "./ThousandPropertiesNewRails.module.scss";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import Image from "@Components/Image";
import CheveronDown from "@Icons/ChevronDown";
import TickIconV2 from "@Icons/TickIconV2";
import Portal from "@Components/Portal";
import PropertyDetailsModal from "@Pages/PropertyPage/components/PropertyDetailsModal";
import HorizontalScroll from "@Components/HorizontalScroll";
import { Pill } from "./types";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsLanguage from "hooks/useIsLanguage";
import useClickOutside from "hooks/useOutsideClick";

interface PillsSelectorProps {
  pills: Pill[];
  activePill: string | null;
  activeCountry: string;
  isCountryMenuOpen: boolean;
  isDesktop: boolean;
  isWhitelabel: boolean;
  isShowPills: boolean;
  countryCityMapping: any;
  countryMenuRef: React.RefObject<HTMLDivElement>;
  setIsCountryMenuOpen: (open: boolean) => void;
  setActiveCountry: (country: string) => void;
  setPills: (pills: Pill[]) => void;
  fetchCityUniData: (pill: Pill, country?: string) => void;
  domesticCountry?: string;
}

const PillsSelector: React.FC<PillsSelectorProps> = ({
  pills,
  activePill,
  activeCountry,
  isCountryMenuOpen,
  isDesktop,
  isWhitelabel,
  isShowPills,
  countryCityMapping,
  countryMenuRef,
  setIsCountryMenuOpen,
  setActiveCountry,
  setPills,
  fetchCityUniData,
  domesticCountry,
}) => {
  const { t } = useTranslationHook();
  const userLanguage = useIsLanguage();
  const ref = useRef(null);

  useClickOutside(ref, () => {
    if (isCountryMenuOpen) setIsCountryMenuOpen(false);
  });
  if (isWhitelabel || !isShowPills) return null;

  // Reorder countries so the user's detected country appears first
  const allCountryKeys = Object.keys(countryCityMapping);
  const orderedCountryKeys =
    domesticCountry && countryCityMapping[domesticCountry]
      ? [domesticCountry, ...allCountryKeys.filter((k) => k !== domesticCountry)]
      : allCountryKeys;

  // Common handlers
  const handleCountrySelect = (countryName: string, e?: any) => {
    e?.stopPropagation();
    setActiveCountry(countryName);
    setPills(countryCityMapping[countryName].city);
    fetchCityUniData(countryCityMapping[countryName].city[0], countryName);
    setIsCountryMenuOpen(false);
  };

  const handleCountryKeyDown = (event: React.KeyboardEvent, countryName: string) => {
    if (event.key === "Enter") {
      handleCountrySelect(countryName);
    }
  };

  const handlePillKeyDown = (event: React.KeyboardEvent, pill: Pill) => {
    if (event.key === "Enter") {
      fetchCityUniData(pill);
    }
  };

  // Common render functions
  const renderCountryButton = (additionalProps: any = {}) => (
    <div
      role="button"
      tabIndex={0}
      ref={countryMenuRef}
      onClick={() => setIsCountryMenuOpen(!isCountryMenuOpen)}
      className={formatClassNames(classes.pillsActive, isDesktop && classes.pillsDesktop)}
      aria-label="Search Country"
      {...additionalProps}
    >
      <div className={formatClassNames(classes.pillsInner)}>
        <Image src={countryCityMapping[activeCountry].icon} height={16} width={16} />
        {userLanguage === "zh" && countryCityMapping[activeCountry]?.name_zh
          ? countryCityMapping[activeCountry]?.name_zh
          : activeCountry || "Recently Viewed"}
        <CheveronDown secondCheveron />
      </div>
    </div>
  );

  const renderCountryOptions = (containerProps: any = {}) => (
    <div {...containerProps}>
      {countryCityMapping &&
        orderedCountryKeys.map((countryName: string) => (
          <div
            key={countryName}
            role="button"
            tabIndex={0}
            onClick={(e) => handleCountrySelect(countryName, e)}
            className={formatClassNames(
              classes.options,
              countryName === activeCountry && classes.active,
            )}
            aria-label={countryName}
            onKeyDown={(event) => handleCountryKeyDown(event, countryName)}
          >
            <span className={classes.flagName}>
              {(countryName !== activeCountry || !isDesktop) && (
                <Image src={countryCityMapping[countryName]?.sq_icon} height={16} width={16} />
              )}
              {userLanguage === "zh" ? countryCityMapping[countryName]?.name_zh : countryName}
            </span>
            {countryName === activeCountry && <TickIconV2 />}
          </div>
        ))}
    </div>
  );

  const renderCityPills = () => (
    <div className={classes.horizontalScrollContainer}>
      <HorizontalScroll
        className={formatClassNames(
          classes.horizontalScrollCr,
          isDesktop ? classes.desktopCr : classes.mobilePillsCity,
        )}
        bgColor="white"
      >
        {pills.map((pill, index) =>
          pill?.name || pill?.canonical_name ? (
            <div
              key={`${pill.name}-${index}`}
              role="button"
              tabIndex={0}
              onClick={() => {
                fetchCityUniData(pill);
                setIsCountryMenuOpen(false);
              }}
              className={formatClassNames(
                pill.name === activePill ? classes.pillsActive : classes.pills,
                isDesktop && classes.pillsDesktop,
              )}
              data-testid={`homepage-${pill.name}-pills`}
              aria-label={pill.name}
              onKeyDown={(event) => handlePillKeyDown(event, pill)}
            >
              {userLanguage === "zh" ? pill.name_zh : pill.name}
            </div>
          ) : (
            <Fragment key={index} />
          ),
        )}
      </HorizontalScroll>
    </div>
  );

  // Mobile view with modal
  const renderMobileView = () => (
    <>
      <div className={formatClassNames(classes.pillsContainer, classes.mobilePillsContainer)}>
        {/* country */}
        {renderCountryButton()}
        {pills.length > 0 && (
          <>
            <div className={classes.seperator} />
            {/* cities */}
            {renderCityPills()}
          </>
        )}
      </div>
      {isCountryMenuOpen && !isDesktop ? (
        <Portal rootId="forPortal">
          <PropertyDetailsModal
            header={t("Select Country")}
            actionButtonText={t("bookingpage.components.modalactionbutton.gotit")}
            bodyWithNoPadding
            hidePropertyModal={(e: any) => {
              e?.stopPropagation();
              setIsCountryMenuOpen(false);
            }}
            removeFooter
          >
            {renderCountryOptions({ className: classes.bottomSheetContainer })}
          </PropertyDetailsModal>
        </Portal>
      ) : (
        <></>
      )}
    </>
  );
  // Desktop view with dropdown
  const renderDesktopView = () => (
    <div className={classes.pillsContainer} style={{ position: "relative" }}>
      {/* country */}
      {renderCountryButton({
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === "Enter") setIsCountryMenuOpen(true);
        },
      })}
      {isCountryMenuOpen && (
        <div
          ref={ref}
          className={classes.CountryContainer}
          style={{ position: "absolute", top: "110%", left: 0, zIndex: 10 }}
        >
          {countryCityMapping &&
            orderedCountryKeys.map((countryName: string) => (
              <div
                key={countryName}
                role="button"
                tabIndex={0}
                onClick={(e) => handleCountrySelect(countryName, e)}
                className={formatClassNames(
                  classes.options,
                  countryName === activeCountry && classes.active,
                )}
                aria-label={countryName}
                onKeyDown={(event) => handleCountryKeyDown(event, countryName)}
              >
                {countryName !== activeCountry && (
                  <Image src={countryCityMapping[countryName]?.sq_icon} height={28} width={28} />
                )}
                {userLanguage === "zh" ? countryCityMapping[countryName]?.name_zh : countryName}
                {countryName === activeCountry && <TickIconV2 />}
              </div>
            ))}
        </div>
      )}

      {pills.length > 0 && (
        <>
          <div className={classes.seperator} />
          {/* cities */}
          {renderCityPills()}
        </>
      )}
    </div>
  );

  return isDesktop ? renderDesktopView() : renderMobileView();
};

export default wrapperHOC(PillsSelector, {
  componentName: "PillsSelector",
  showForChina: true,
});
